/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Chip,
  Paper,
  TextField,
  Typography,
  autocompleteClasses,
  colors,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';

import { MerchantAccess } from '../../../store/modules/merchant/types';
import { isValidUUIDv4 } from '../../../util/helpers';
import { useMerchant } from '../../../contexts/MerchantContext';
import { useLazyGetMerchantsByUserQuery } from '../../../store/modules/merchant/api';
import { getAccessTokenWithAudience } from '../../../util/auth0Utils';
import Logger from '../../../util/logger';
import { useDebounce } from '../../../hooks/useDebounce';

const PaperComponent = ({ children }: React.HTMLAttributes<HTMLElement>) => (
  <Paper
    variant="outlined"
    sx={{
      border: `1px solid ${colors.grey[400]}`,
      fontSize: '14px',
      maxHeight: '220px',
      overflow: 'auto',
      [`& .${autocompleteClasses.listbox}`]: {
        maxHeight: 'initial',
        backgroundColor: 'background.paper',
      },
    }}
  >
    {children}
  </Paper>
);

type Props = {
  value: MerchantAccess[];
  onChange: (merchants: MerchantAccess[]) => void;
  label?: string;
  disabled?: boolean;
  /** When merchant access is disabled for Skipify-internal roles, only Skipify admins see this hint. */
  showSkipifyInternalMerchantHint?: boolean;
};

export const MerchantAccessMultiSelect = ({
  value,
  onChange,
  label = 'Merchant access',
  disabled = false,
  showSkipifyInternalMerchantHint = false,
}: Props) => {
  const { merchantUserId } = useMerchant();
  const { getAccessTokenSilently } = useAuth0();
  const [getMerchantsByUser, { isFetching: isSearchLoading }] = useLazyGetMerchantsByUserQuery();
  const [merchantResults, setMerchantResults] = useState<MerchantAccess[]>([]);
  const [inputValue, setInputValue] = useState('');

  const debouncedSearchMerchants = useDebounce(async (searchTerm: string) => {
    if (!getAccessTokenSilently || !merchantUserId || !searchTerm.trim()) {
      setMerchantResults([]);
      return;
    }

    try {
      const token = await getAccessTokenWithAudience(getAccessTokenSilently);
      const results = await getMerchantsByUser({
        userId: merchantUserId,
        token,
        params: isValidUUIDv4(searchTerm)
          ? { merchantId: searchTerm, pageNumber: 1, pageSize: 250 }
          : { merchantName: searchTerm, pageNumber: 1, pageSize: 250 },
      }).unwrap();
      setMerchantResults(results?.data ?? []);
    } catch (error) {
      Logger.error('Error searching merchants:', error);
      setMerchantResults([]);
    }
  }, 300);

  const options = useMemo(() => {
    const selectedIds = new Set(value.map((merchant) => merchant.merchantId));
    return merchantResults.filter((merchant) => !selectedIds.has(merchant.merchantId));
  }, [merchantResults, value]);

  return (
    <Autocomplete
      multiple
      disabled={disabled}
      loading={isSearchLoading}
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === 'input') {
          debouncedSearchMerchants(newInputValue);
        }
      }}
      onChange={(_event, newValue) => onChange(newValue)}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, selected) => option.merchantId === selected.merchantId}
      PaperComponent={PaperComponent}
      noOptionsText={inputValue.trim() ? 'No merchants found' : 'Search by merchant name or ID'}
      loadingText="Searching for merchants..."
      filterOptions={(opts) => opts}
      renderOption={(props, option) => (
        <li {...props} key={option.merchantId}>
          <Typography sx={{ fontSize: '14px' }}>
            {option.name}
            <Typography component="span" sx={{ color: 'text.secondary', ml: 1, fontSize: 12 }}>
              {option.merchantId}
            </Typography>
          </Typography>
        </li>
      )}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} key={option.merchantId} label={option.name} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={disabled ? undefined : 'Search merchants'}
          helperText={
            disabled && showSkipifyInternalMerchantHint
              ? 'Merchant access is not used for SkipifyAdmin / SkipifyViewer'
              : disabled
                ? undefined
                : 'Search by merchant name or merchant ID'
          }
        />
      )}
    />
  );
};
